import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconClose from '@apollo/icons/default/IconClose.svg';
import { IconButton } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useEffect, useRef } from 'react';

import { PlainHtmlButton } from 'src/components/common/plainHtmlButton/PlainHtmlButton';

interface Props {
  className?: string;
  title?: string;
  tags: Set<string>;
  erroredTags?: Set<string>;
  onTagRemoved: (tag: string) => void;
  disabled?: boolean;
}

function TagListItem({
  tag,
  disabled,
  showError,
  onRemoveClick,
}: {
  tag: string;
  disabled: boolean;
  showError: boolean;
  onRemoveClick: React.MouseEventHandler<HTMLElement>;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    // Using event.stopPropagation() in React's onClick doesn't work here,
    // since SpaceKit's modal is attached to a native DOM event.
    // Useful context:
    // https://gideonpyzer.dev/blog/2018/12/29/event-propagation-react-synthetic-events-vs-native-events/
    const buttonRefCurrent = buttonRef.current;
    const handleMouseDown = (event: MouseEvent) => event.stopPropagation();
    if (buttonRefCurrent) {
      buttonRefCurrent.addEventListener('mousedown', handleMouseDown);
    }
    return () => {
      if (buttonRefCurrent) {
        buttonRefCurrent.removeEventListener('mousedown', handleMouseDown);
      }
    };
  }, []);
  return (
    <span
      key={tag}
      data-testid={`taglist-item-${tag}`}
      className={classnames(
        'flex items-center rounded-sm pl-2 text-sm font-semibold',
        {
          'bg-blue-lightest text-link': !showError && !disabled,
          'bg-red-lightest text-red': showError,
          'bg-silver text-secondary': disabled,
        },
      )}
    >
      {tag}{' '}
      <IconButton
        aria-label="remove this variant"
        variant="hidden"
        size="xs"
        className="ml-1 hover:bg-transparent"
        ref={buttonRef}
        tabIndex={0}
        icon={
          <IconClose
            className={classnames({
              '!text-link': !showError && !disabled,
              '!text-red-light': showError,
              '!text-placeholder': disabled,
            })}
          />
        }
        onClick={onRemoveClick}
        isDisabled={disabled}
      />
    </span>
  );
}

export const TagList = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      className,
      title = 'Select tag(s)',
      tags,
      erroredTags,
      onTagRemoved,
      disabled = false,
      ...other
    },
    ref,
  ) => {
    return (
      <PlainHtmlButton
        {...other}
        ref={ref}
        className={classnames(
          'focus-visible:focus-ring flex w-full items-center rounded border px-3 py-2',
          className,
          {
            'border-silver-darker hover:border-blue-light focus:border-blue-light':
              (erroredTags?.size ?? 0) === 0 && !disabled,
            'border-red hover:border-red focus:border-red':
              (erroredTags?.size ?? 0) > 0,
            'border-silver hover:border-silver focus:border-silver': disabled,
          },
        )}
      >
        {tags.size > 0 && (
          <div className="flex flex-6 flex-wrap items-center gap-2">
            {Array.from(tags).map((tag) => (
              <TagListItem
                key={tag}
                tag={tag}
                showError={erroredTags?.has(tag) ?? false}
                onRemoveClick={() => {
                  onTagRemoved(tag);
                }}
                disabled={disabled}
              />
            ))}
          </div>
        )}
        <div className="flex flex-1 items-center">
          {tags.size === 0 ? (
            <span className="flex-1 text-left text-secondary">{title}</span>
          ) : (
            <span className="flex-1 text-right text-secondary">
              {tags.size}&nbsp;selected
            </span>
          )}
          <IconChevronDown className="ml-3" />
        </div>
      </PlainHtmlButton>
    );
  },
);
